import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Headset, Volume2, VolumeX, Sun as SunIcon, Rocket, BarChart3, X, MapPin, Eye } from "lucide-react";
import { Toaster } from "../components/ui/sonner";
import CanvasFrame from "../three/CanvasFrame";
import TabSealHeader from "../components/hud/TabSealHeader";
import ViewportSwitcher from "../components/hud/ViewportSwitcher";
import BiometricSleepHUD from "../components/hud/BiometricSleepHUD";
import RadiometricMoonHUD from "../components/hud/RadiometricMoonHUD";
import TemporalScrubberDock from "../components/hud/TemporalScrubberDock";
import PresetShareModal from "../components/hud/PresetShareModal";
import { computeSim, biometricSeries, PRESETS, TIMELINE } from "../lib/eclipse";
import { audio } from "../lib/audio";
import { xrStore } from "../lib/xrstore";
import { preloadTextures } from "../lib/textures";

const SPEEDS = [1, 2, 4];
const DISTRICTS = [
  "Meridian Heights", "Umbra Financial District", "Aldrin Waterfront",
  "Copperline Quarter", "Old Observatory Row", "Penumbra Gardens", "Syzygy Square",
];

export default function Observatory() {
  const [timeMin, setTimeMin] = useState(TIMELINE.max);
  const [danjon, setDanjon] = useState(2.6);
  const [bortle, setBortle] = useState(1);
  const [viewport, setViewport] = useState("aerial");
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState(1);
  const [presetId, setPresetId] = useState("e2026");
  const [muted, setMuted] = useState(false);
  const [flying, setFlying] = useState(false);
  const [perspective, setPerspective] = useState("orbit");
  const [loading, setLoading] = useState(true);
  const [diveTarget, setDiveTarget] = useState(null);
  const [dataOpen, setDataOpen] = useState(false);
  const [districtLabel, setDistrictLabel] = useState("");
  const [ambientVol, setAmbientVol] = useState(0.7);
  const [hudHidden, setHudHidden] = useState(false);

  useEffect(() => {
    let done = false;
    const finish = () => { if (!done) { done = true; setLoading(false); } };
    preloadTextures().then(finish).catch(finish);
    const failsafe = setTimeout(finish, 1500); // never hang on the loader
    return () => clearTimeout(failsafe);
  }, []);

  const b0 = useMemo(() => PRESETS.find((p) => p.id === presetId)?.b0 ?? PRESETS[0].b0, [presetId]);
  const sim = useMemo(() => computeSim(timeMin, danjon, bortle, b0), [timeMin, danjon, bortle, b0]);
  const series = useMemo(() => biometricSeries(danjon, bortle, b0), [danjon, bortle, b0]);

  const simRef = useRef(sim);
  simRef.current = sim;

  // Live refs read by the in-scene OrbitalClock (drives motion without re-renders) --
  const timeRef = useRef(timeMin);
  const dirRef = useRef(1);
  const playingRef = useRef(playing);
  const speedRef = useRef(speed);
  const danjonRef = useRef(danjon);
  const bortleRef = useRef(bortle);
  const b0Ref = useRef(b0);
  playingRef.current = playing;
  speedRef.current = speed;
  danjonRef.current = danjon;
  bortleRef.current = bortle;
  b0Ref.current = b0;

  const commitTime = useCallback((t) => setTimeMin(t), []);
  const handleTime = useCallback((v) => { setPlaying(false); timeRef.current = v; setTimeMin(v); }, []);

  const clock = useMemo(
    () => ({ timeRef, dirRef, playingRef, speedRef, danjonRef, bortleRef, b0Ref, onCommit: commitTime }),
    [commitTime]
  );

  // ambient audio bed — swaps between deep-space drone and city hum by viewport ----
  useEffect(() => {
    audio.startAmbient(viewport === "ground" ? "ground" : "space");
  }, [viewport]);

  // spatial "fly" transition when changing perspective ----------------------
  const changeView = useCallback((v) => {
    if (v === viewport) return;
    audio.resume();
    audio.swell();
    setDiveTarget(null);
    setFlying(true);
    if (v === "ground") setDistrictLabel((prev) => prev || "Metropolis Core");
    setTimeout(() => setViewport(v), 260);
    setTimeout(() => setFlying(false), 1050);
  }, [viewport]);

  // click a continent/city on the giant Earth -> cinematic dive to the ground view
  const handleEarthClick = useCallback((point) => {
    if (viewport !== "aerial") return;
    audio.resume();
    audio.swell();
    const idx = Math.abs(Math.round(point.x * 7 + point.z * 13 + point.y * 3)) % DISTRICTS.length;
    setDistrictLabel(DISTRICTS[idx]);
    setDiveTarget([point.x, point.y, point.z]);
    setTimeout(() => setFlying(true), 520);
    setTimeout(() => setViewport("ground"), 780);
    setTimeout(() => { setFlying(false); setDiveTarget(null); }, 1300);
  }, [viewport]);

  const toggleMute = () => { const m = !muted; setMuted(m); audio.setMuted(m); if (!m) audio.resume(); };
  const onAmbientVol = (e) => { const v = parseFloat(e.target.value); setAmbientVol(v); audio.setAmbientVolume(v); audio.resume(); };

  const onPreset = (id) => {
    const p = PRESETS.find((x) => x.id === id);
    setPresetId(id);
    if (p) { setDanjon(p.danjon); handleTime(TIMELINE.max); }
  };

  const onLoadPreset = (rec) => {
    handleTime(rec.time_minutes);
    setDanjon(rec.danjon);
    setBortle(rec.bortle);
    if (rec.viewport) changeView(rec.viewport);
  };

  const enterVR = async () => { audio.resume(); try { await xrStore.enterVR(); } catch (e) {} };

  if (loading) {
    return (
      <div className="loader-screen" data-testid="loading-screen">
        <div className="loader-seal">
          <img src="https://customer-assets-m6fa6gv7.emergentagent.net/job_orbital-geometry-lab/artifacts/5x3imyr4_TAB%20blue%20background%20gold%20seal.png" alt="TAB Seal" />
        </div>
        <div className="loader-title">The Earth's Shadow</div>
        <div className="loader-sub">Calibrating celestial topography…</div>
        <div className="loader-bar"><span /></div>
      </div>
    );
  }

  const renderCanvas = () => {
    if (viewport === "split") {
      return (
        <div className="split-grid">
          <CanvasFrame view="aerial" simRef={simRef} bortle={bortle} withXR perspective={perspective} narrow testid="canvas-aerial" primary clock={clock} onEarthClick={handleEarthClick} diveTarget={diveTarget} />
          <div className="split-divider" />
          <CanvasFrame view="ground" simRef={simRef} bortle={bortle} testid="canvas-ground" />
        </div>
      );
    }
    if (viewport === "ground") return <CanvasFrame view="ground" simRef={simRef} bortle={bortle} testid="canvas-ground" primary clock={clock} />;
    return <CanvasFrame view="aerial" simRef={simRef} bortle={bortle} withXR perspective={perspective} testid="canvas-aerial" primary clock={clock} onEarthClick={handleEarthClick} diveTarget={diveTarget} />;
  };

  return (
    <div className={`observatory${hudHidden ? " hud-off" : ""}`} data-testid="observatory-root">
      {/* 3D layer */}
      <div className="scene-layer" key={viewport}>{renderCanvas()}</div>

      {/* grain + vignette overlays */}
      <div className="grain" />
      <div className="vignette" />

      {/* fly transition */}
      <AnimatePresence>
        {flying && (
          <motion.div
            className="fly-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.9, 0] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.05, times: [0, 0.35, 1] }}
          />
        )}
      </AnimatePresence>

      {/* Top bar */}
      <div className="top-bar">
        <TabSealHeader />
        <ViewportSwitcher value={viewport} onChange={changeView} />
        <div className="flex items-center gap-2">
          {viewport !== "ground" && (
            <button
              className={`action-btn ${perspective === "solar" ? "action-btn-active" : ""}`}
              data-testid="solar-perspective-toggle"
              onClick={() => setPerspective((p) => (p === "solar" ? "orbit" : "solar"))}
              title="Solar Perspective — look back at the Sun from behind Earth"
            >
              <SunIcon size={15} /><span className="hidden lg:inline">{perspective === "solar" ? "Orbit View" : "Solar View"}</span>
            </button>
          )}
          <button className="action-btn" data-testid="vr-enter-btn" onClick={enterVR} title="Enter WebXR">
            <Headset size={15} /><span className="hidden lg:inline">Enter Orbit</span>
          </button>
          <button className="action-btn" data-testid="audio-toggle" onClick={toggleMute} title="Audio">
            {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
          </button>
          <input
            type="range" min="0" max="1" step="0.01"
            value={ambientVol}
            onChange={onAmbientVol}
            className="ambient-dial hidden md:inline-block"
            data-testid="ambient-volume-dial"
            title="Ambient sound bed volume"
            aria-label="Ambient volume"
          />
          <button
            className={`action-btn ${dataOpen ? "action-btn-active" : ""}`}
            data-testid="data-toggle"
            onClick={() => setDataOpen((v) => !v)}
            title="Telemetry & Biometrics"
          >
            <BarChart3 size={15} /><span className="hidden lg:inline">Data</span>
          </button>
          <PresetShareModal state={{ timeMin, danjon, bortle, viewport }} onLoad={onLoadPreset} />
        </div>
      </div>

      {/* mobile viewport switch */}

      {/* Back to Space — appears on the ground to fly back out to the macro globe */}
      <AnimatePresence>
        {viewport === "ground" && (
          <motion.button
            data-testid="back-to-space-btn"
            className="back-to-space"
            onClick={() => changeView("aerial")}
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45 }}
            title="Fly back out to the orbital macro view"
          >
            <Rocket size={15} /><span>Back to Space</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* District / landmark caption when on the ground */}
      <AnimatePresence>
        {viewport === "ground" && districtLabel && (
          <motion.div
            data-testid="district-label"
            className="district-label"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5 }}
          >
            <MapPin size={13} />
            <span>{districtLabel}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Collapsible telemetry sidebar (Biometrics + Radiometry) */}
      <AnimatePresence>
        {dataOpen && (
          <motion.aside
            data-testid="data-sidebar"
            className="data-sidebar"
            initial={{ x: 400, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 400, opacity: 0 }}
            transition={{ type: "tween", duration: 0.32, ease: "easeInOut" }}
          >
            <div className="data-sidebar-head">
              <span className="hud-kicker">Live Telemetry</span>
              <button className="dock-icon-btn" data-testid="data-close" onClick={() => setDataOpen(false)}>
                <X size={16} />
              </button>
            </div>
            <div className="data-sidebar-body">
              <BiometricSleepHUD sim={sim} series={series} />
              <RadiometricMoonHUD sim={sim} />
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Bottom dock */}
      <div className="dock-wrap">
        <TemporalScrubberDock
          timeMin={timeMin}
          onTime={handleTime}
          playing={playing}
          onPlay={() => { audio.resume(); setPlaying((p) => !p); }}
          speed={speed}
          onSpeed={() => setSpeed((s) => SPEEDS[(SPEEDS.indexOf(s) + 1) % SPEEDS.length])}
          danjon={danjon}
          onDanjon={setDanjon}
          bortle={bortle}
          onBortle={setBortle}
          presetId={presetId}
          onPreset={onPreset}
          coverage={sim.coverage}
          onHideHud={() => setHudHidden(true)}
        />
      </div>

      {/* Restore HUD when hidden */}
      {hudHidden && (
        <button
          className="hud-restore"
          data-testid="show-hud-btn"
          onClick={() => setHudHidden(false)}
          title="Show controls"
        >
          <Eye size={18} />
        </button>
      )}

      <Toaster position="top-center" theme="dark" />
    </div>
  );
}
